// askSmart-ui/src/pages/smrtdocsServices.ts
//
// How a SMRT Docs service's document lines are worked out, in one place.
//
// A Sub Service holds only its OWN lines. The lines it shares with its family
// live on its Master and are merged in here, at read time, every time. Nothing
// is copied at creation: an admin adding a document to the Master is meant to
// reach every Sub Service under it, and a copy taken once would stop tracking
// it the moment either side was touched.
//
// The merge deliberately mirrors getMergedSteps in SMRTDocsTrafficModal, which
// does the same job one level down — a per-USER overlay on a service. Same
// shape, same rules, one level up: `attachedFields` hangs extra documents off
// an inherited category, `steps` adds categories of the child's own.
//
// Every caller that reads `service.data.steps` directly is reading a Sub
// Service as empty. Go through resolveServiceSteps instead.

export interface ServiceLike {
  /** Permanent `svc_…` id. Never a position — those shift on delete. */
  id?: string;
  name?: string;
  data?: any;
  /** An abstract template. Never assignable to a user. */
  isMaster?: boolean;
  /** The Master's id, on a Sub Service. */
  parentId?: string;
  /** A Sub Service's own half of its name. */
  subName?: string;
}

/** A Sub Service's own content, in the shape the editor's local mode speaks. */
export interface OwnContent {
  attachedFields: Record<string, any[]>;
  steps: any[];
  /**
   * This Sub Service's own version of inherited document lines, by field key.
   *
   * A Sub Service is edited like any other service — rename a line, change its
   * type, make it mandatory, hide it — and none of that reaches the Master or
   * its siblings. What is stored is only the DIFFERENCE: anything the child
   * hasn't changed still comes from the Master and still moves when the Master
   * moves, which is the whole point of the family.
   *
   * `{ hidden: true }` is how a child drops a line it doesn't need — the fire
   * safety plan one tenant owes and another doesn't. Hiding on the MASTER is a
   * different, wider act, lives on the field itself, and cannot be undone from
   * a child.
   */
  fieldOverrides: Record<string, any>;
}

export const isSubService = (svc: ServiceLike | null | undefined): boolean =>
  !!svc?.parentId;

/**
 * The Master a Sub Service hangs from, or null.
 *
 * Null is a real answer, not only an error: a Master can be deleted out from
 * under its children. Callers render the child's own lines in that case rather
 * than nothing, so a dangling parentId costs the inherited documents and not
 * the whole service.
 */
export const findMaster = (
  services: ServiceLike[],
  svc: ServiceLike | null | undefined,
): ServiceLike | null => {
  if (!svc?.parentId) return null;
  return services.find((s) => s?.id === svc.parentId) || null;
};

/** A Sub Service's own lines, defaulted so callers never guard both fields. */
export const ownContent = (svc: ServiceLike | null | undefined): OwnContent => ({
  attachedFields:
    svc?.data?.attachedFields && typeof svc.data.attachedFields === "object"
      ? svc.data.attachedFields
      : {},
  steps: Array.isArray(svc?.data?.steps) ? svc!.data.steps : [],
  // Absent on every Sub Service created before this existed, which reads as
  // "nothing changed from the Master" — the right default, since the
  // alternative would alter records nobody touched.
  fieldOverrides:
    svc?.data?.fieldOverrides && typeof svc.data.fieldOverrides === "object"
      ? svc.data.fieldOverrides
      : {},
});

/**
 * Inherited categories first, each carrying any documents the child has hung
 * off it, then the child's own categories.
 *
 * The inherited steps are returned as fresh objects rather than the Master's
 * own, so a caller that mutates what it renders can't write into the Master
 * through a Sub Service.
 */
/** Categories with their hidden document lines removed. */
const withoutHidden = (steps: any[]): any[] =>
  (steps || []).map((step: any) => ({
    ...step,
    fields: (step?.fields || []).filter((field: any) => !field?.hidden),
  }));

export const mergeSubServiceSteps = (
  masterSteps: any[],
  own: OwnContent,
): any[] => {
  const overrides = own.fieldOverrides || {};
  const merged = (masterSteps || []).map((step: any) => {
    const attached = own.attachedFields?.[step?.id] || [];
    const kept = (step?.fields || [])
      // Withdrawn by the Master. Filtered BEFORE the child's overrides are
      // applied, so no child can put back a line the Master took away.
      .filter((field: any) => !field?.hidden)
      // The child's own version of the line. Only what it changed is stored,
      // so everything else still tracks the Master.
      .map((field: any) => ({
        ...field,
        ...(overrides[field?.key] || {}),
        // The key is the line's identity and what documents are filed
        // against — an override must never be able to move it.
        key: field?.key,
      }))
      // Dropped by this child alone.
      .filter((field: any) => !field?.hidden);
    return { ...step, fields: [...kept, ...attached] };
  });
  return [...merged, ...(own.steps || [])];
};

/**
 * Every document line a service shows, whoever defined it.
 *
 * Takes the whole list because a Sub Service is resolved against its Master,
 * which is a sibling in that same array.
 */
export const resolveServiceSteps = (
  services: ServiceLike[],
  svc: ServiceLike | null | undefined,
): any[] => {
  if (!svc) return [];
  // A hidden line is dropped on the way out, not deleted on the way in — the
  // editor still shows it, struck through with its H lit, so it can come back
  // and so documents already filed against it aren't orphaned.
  if (!svc.parentId) return withoutHidden(svc.data?.steps || []);
  const master = findMaster(services, svc);
  const own = ownContent(svc);
  if (!master) return withoutHidden(own.steps);
  return mergeSubServiceSteps(master.data?.steps || [], own);
};

/** The same, addressed by position — what most callers already hold. */
export const resolveServiceStepsAt = (
  services: ServiceLike[],
  index: number,
): any[] => resolveServiceSteps(services, services?.[index]);
